import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { get } from "../helper/APIClient";
import { Routes } from "../helper/Routes";
import { CategoryData } from "../helper/interface";
import "./GlobalCardSearch.css";

const SEARCH_PAGE_SIZE = 100;
type SearchTab = "dataCards" | "templates";

type SearchableCard = {
  idDataCard: string;
  idCardTemplate: string;
  cardName: string;
  templateName: string;
  categoryName: string;
  idCategory?: string;
};

type SearchableTemplate = {
  idCardTemplate: string;
  idCardTemplateStr?: string;
  idCategory?: string;
  templateName: string;
  categoryName?: string;
  isProbate?: boolean;
};

type CardSearchResult = {
  cardId: string;
  cardName: string;
  templateName: string;
  categoryName: string;
  category?: CategoryData;
};

type TemplateSearchResult = {
  template: SearchableTemplate;
  category?: CategoryData;
  categoryName: string;
};

interface GlobalCardSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const renderHighlightedText = (value: string, query: string): React.ReactNode => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return value;
  }

  const normalizedValue = value.toLowerCase();
  const normalizedQuery = trimmedQuery.toLowerCase();
  const matchIndex = normalizedValue.indexOf(normalizedQuery);

  if (matchIndex < 0) {
    return value;
  }

  const before = value.slice(0, matchIndex);
  const match = value.slice(matchIndex, matchIndex + trimmedQuery.length);
  const after = value.slice(matchIndex + trimmedQuery.length);

  return (
    <>
      {before}
      <mark className="global-card-search__highlight">{match}</mark>
      {after}
    </>
  );
};

const buildFallbackCategory = (template: SearchableTemplate): CategoryData | undefined => {
  const categoryId = template.idCategory?.trim();
  const categoryName = template.categoryName?.trim();

  if (!categoryId || !categoryName) {
    return undefined;
  }

  return {
    idCategory: 0,
    id_Category: categoryId,
    categoryName,
    colourStart: "#f8fafc",
    colourEnd: "#eef2ff",
    lineColour: "#cbd5e1",
    folderSVG: "",
    iconSVG: "",
    sortOrder: 0,
    cardCount: 0,
  };
};

const buildFallbackCardCategory = (card: SearchableCard): CategoryData | undefined => {
  const categoryId = card.idCategory?.trim();
  const categoryName = card.categoryName?.trim();

  if (!categoryId || !categoryName) {
    return undefined;
  }

  return {
    idCategory: 0,
    id_Category: categoryId,
    categoryName,
    colourStart: "#f8fafc",
    colourEnd: "#eef2ff",
    lineColour: "#cbd5e1",
    folderSVG: "",
    iconSVG: "",
    sortOrder: 0,
    cardCount: 0,
  };
};

const SearchIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const GlobalCardSearch: React.FC<GlobalCardSearchProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const lastRequestIdRef = useRef(0);
  const [query, setQuery] = useState("");
  const [cards, setCards] = useState<SearchableCard[]>([]);
  const [templates, setTemplates] = useState<SearchableTemplate[]>([]);
  const [activeTab, setActiveTab] = useState<SearchTab>("dataCards");
  const [dataCardTotalCount, setDataCardTotalCount] = useState(0);
  const [templateTotalCount, setTemplateTotalCount] = useState(0);
  const [hasMoreDataCards, setHasMoreDataCards] = useState(false);
  const [hasMoreTemplates, setHasMoreTemplates] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMoreDataCards, setIsLoadingMoreDataCards] = useState(false);
  const [isLoadingMoreTemplates, setIsLoadingMoreTemplates] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const deferredQuery = useDeferredValue(query);

  const mapCards = (responseCards?: Array<{
    idDataCard?: string;
    idCardTemplate?: string;
    cardName?: string;
    templateName?: string;
    categoryName?: string;
    idCategory?: string;
  }>): SearchableCard[] =>
    (responseCards ?? []).map((card) => ({
      idDataCard: card.idDataCard ?? "",
      idCardTemplate: card.idCardTemplate ?? "",
      cardName: card.cardName?.trim() || "Untitled card",
      templateName: card.templateName?.trim() || "Unknown template",
      categoryName: card.categoryName?.trim() || "Uncategorised",
      idCategory: card.idCategory ?? "",
    }));

  const mapTemplates = (responseTemplates?: Array<{
    idCardTemplate?: string;
    idCardTemplateStr?: string;
    templateName?: string;
    categoryName?: string;
    idCategory?: string;
    isProbate?: boolean;
  }>): SearchableTemplate[] =>
    (responseTemplates ?? []).map((template) => ({
      idCardTemplate: template.idCardTemplate ?? "",
      idCardTemplateStr: template.idCardTemplateStr ?? "",
      idCategory: template.idCategory ?? "",
      templateName: template.templateName?.trim() || "Untitled template",
      categoryName: template.categoryName?.trim() || "Uncategorised",
      isProbate: Boolean(template.isProbate),
    }));

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setQuery("");
    setCards([]);
    setTemplates([]);
    setActiveTab("dataCards");
    setDataCardTotalCount(0);
    setTemplateTotalCount(0);
    setHasMoreDataCards(false);
    setHasMoreTemplates(false);
    setSearchError(null);
    setIsSearching(false);
    setIsLoadingMoreDataCards(false);
    setIsLoadingMoreTemplates(false);

    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const normalizedQuery = deferredQuery.trim();
    if (!normalizedQuery) {
      setCards([]);
      setTemplates([]);
      setDataCardTotalCount(0);
      setTemplateTotalCount(0);
      setHasMoreDataCards(false);
      setHasMoreTemplates(false);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    const requestId = ++lastRequestIdRef.current;
    const timeoutId = window.setTimeout(() => {
      setIsSearching(true);
      setSearchError(null);
      const includeDataCards = activeTab === "dataCards";
      const includeTemplates = activeTab === "templates";

      void get<{
        dataCards?: Array<{
          idDataCard?: string;
          idCardTemplate?: string;
          cardName?: string;
          templateName?: string;
          categoryName?: string;
          idCategory?: string;
        }>;
        templates?: Array<{
          idCardTemplate?: string;
          idCardTemplateStr?: string;
          templateName?: string;
          categoryName?: string;
          idCategory?: string;
          isProbate?: boolean;
        }>;
        dataCardTotalCount?: number;
        templateTotalCount?: number;
        hasMoreDataCards?: boolean;
        hasMoreTemplates?: boolean;
      }>(Routes.SEARCH_CARD_METADATA, undefined, {
        q: normalizedQuery,
        limit: SEARCH_PAGE_SIZE,
        cardOffset: 0,
        templateOffset: 0,
        includeDataCards,
        includeTemplates,
      })
        .then((response) => {
          if (requestId !== lastRequestIdRef.current) {
            return;
          }

          if (includeDataCards) {
            setCards(mapCards(response.dataCards));
            setDataCardTotalCount(response.dataCardTotalCount ?? 0);
            setHasMoreDataCards(Boolean(response.hasMoreDataCards));
          }

          if (includeTemplates) {
            setTemplates(mapTemplates(response.templates));
            setTemplateTotalCount(response.templateTotalCount ?? 0);
            setHasMoreTemplates(Boolean(response.hasMoreTemplates));
          }
        })
        .catch((error) => {
          if (requestId !== lastRequestIdRef.current) {
            return;
          }

          console.error("Failed to search cards and templates", error);
          setCards([]);
          setTemplates([]);
          setDataCardTotalCount(0);
          setTemplateTotalCount(0);
          setHasMoreDataCards(false);
          setHasMoreTemplates(false);
          setSearchError("Search is temporarily unavailable.");
        })
        .finally(() => {
          if (requestId === lastRequestIdRef.current) {
            setIsSearching(false);
          }
        });
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeTab, deferredQuery, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const cardResults = useMemo<CardSearchResult[]>(
    () =>
      cards.map((card) => {
        return {
          cardId: card.idDataCard,
          cardName: card.cardName,
          templateName: card.templateName,
          categoryName: card.categoryName,
          category: buildFallbackCardCategory(card),
        };
      }),
    [cards]
  );

  const templateResults = useMemo<TemplateSearchResult[]>(
    () =>
      templates.map((template) => {
        const category = buildFallbackCategory(template);

        return {
          template,
          category,
          categoryName: template.categoryName?.trim() || category?.categoryName || "Uncategorised",
        };
      }),
    [templates]
  );

  const loadMoreDataCards = async () => {
    const normalizedQuery = deferredQuery.trim();
    if (!normalizedQuery || isLoadingMoreDataCards || !hasMoreDataCards) {
      return;
    }

    setIsLoadingMoreDataCards(true);
    setSearchError(null);

    try {
      const response = await get<{
        dataCards?: Array<{
          idDataCard?: string;
          idCardTemplate?: string;
          cardName?: string;
          templateName?: string;
          categoryName?: string;
          idCategory?: string;
        }>;
        dataCardTotalCount?: number;
        hasMoreDataCards?: boolean;
      }>(Routes.SEARCH_CARD_METADATA, undefined, {
        q: normalizedQuery,
        limit: SEARCH_PAGE_SIZE,
        cardOffset: cards.length,
        includeTemplates: false,
      });

      setCards((previous) => [...previous, ...mapCards(response.dataCards)]);
      setDataCardTotalCount(response.dataCardTotalCount ?? dataCardTotalCount);
      setHasMoreDataCards(Boolean(response.hasMoreDataCards));
    } catch (error) {
      console.error("Failed to load more data cards", error);
      setSearchError("Search is temporarily unavailable.");
    } finally {
      setIsLoadingMoreDataCards(false);
    }
  };

  const loadMoreTemplates = async () => {
    const normalizedQuery = deferredQuery.trim();
    if (!normalizedQuery || isLoadingMoreTemplates || !hasMoreTemplates) {
      return;
    }

    setIsLoadingMoreTemplates(true);
    setSearchError(null);

    try {
      const response = await get<{
        templates?: Array<{
          idCardTemplate?: string;
          idCardTemplateStr?: string;
          templateName?: string;
          categoryName?: string;
          idCategory?: string;
          isProbate?: boolean;
        }>;
        templateTotalCount?: number;
        hasMoreTemplates?: boolean;
      }>(Routes.SEARCH_CARD_METADATA, undefined, {
        q: normalizedQuery,
        limit: SEARCH_PAGE_SIZE,
        templateOffset: templates.length,
        includeDataCards: false,
      });

      setTemplates((previous) => [...previous, ...mapTemplates(response.templates)]);
      setTemplateTotalCount(response.templateTotalCount ?? templateTotalCount);
      setHasMoreTemplates(Boolean(response.hasMoreTemplates));
    } catch (error) {
      console.error("Failed to load more card templates", error);
      setSearchError("Search is temporarily unavailable.");
    } finally {
      setIsLoadingMoreTemplates(false);
    }
  };

  const handleExistingCardOpen = (result: CardSearchResult) => {
    navigate(
      { pathname: "/card-details", search: location.search },
      {
        state: {
          cardDataId: result.cardId,
          cardDataName: result.cardName,
          cardTemplateName: result.templateName,
          category: result.category,
        },
      }
    );

    onClose();
  };

  const handleCreateCard = (result: TemplateSearchResult) => {
    navigate(
      { pathname: "/card-details", search: location.search },
      {
        state: {
          cardTemplateId: result.template,
          cardTemplateName: result.template.templateName,
          category: result.category,
          showCreateCard: true,
        },
      }
    );

    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const hasSearchTerm = deferredQuery.trim().length > 0;
  const showDataCards = activeTab === "dataCards";
  const normalizedQuery = query.trim();
  const normalizedDeferredQuery = deferredQuery.trim();
  const isWaitingForDebounce = normalizedQuery.length > 0 && normalizedQuery !== normalizedDeferredQuery;
  const isTabLoading = normalizedQuery.length > 0 && (isWaitingForDebounce || isSearching);
  const searchPlaceholder = showDataCards ? "Search card names" : "Search templates";

  return (
    <div className="global-card-search" role="dialog" aria-modal="true" aria-labelledby="global-card-search-title">
      <div className="global-card-search__backdrop" aria-hidden="true" />

      <div className="global-card-search__panel">
        <div className="global-card-search__header">
          <div>
            <h2 id="global-card-search-title">Search your vault</h2>
            <p>Find existing cards by card name or start a new one from a matching template.</p>
          </div>

          <button type="button" className="global-card-search__close" onClick={onClose} aria-label="Close search">
            Close
          </button>
        </div>

        {searchError ? (
          <div className="global-card-search__status global-card-search__status--error">{searchError}</div>
        ) : (
          <div className="global-card-search__results">
            <div className="global-card-search__tabs" role="tablist" aria-label="Search result type">
              <button
                type="button"
                role="tab"
                aria-selected={showDataCards}
                className={`global-card-search__tab ${showDataCards ? "global-card-search__tab--active" : ""}`}
                onClick={() => setActiveTab("dataCards")}
              >
                Data Cards
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!showDataCards}
                className={`global-card-search__tab ${!showDataCards ? "global-card-search__tab--active" : ""}`}
                onClick={() => setActiveTab("templates")}
              >
                Add New Cards
              </button>
            </div>

            <label className="global-card-search__input-shell" htmlFor="global-card-search-input">
              <SearchIcon className="global-card-search__input-icon" />
              <input
                ref={inputRef}
                id="global-card-search-input"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                autoComplete="off"
              />
            </label>

            {showDataCards ? (
              <section className="global-card-search__section global-card-search__section--single" aria-labelledby="global-card-search-cards">
                <div className="global-card-search__section-header">
                  <h3 id="global-card-search-cards">Data Cards</h3>
                  <span>
                    {isTabLoading
                      ? "Searching..."
                      : hasSearchTerm
                        ? `${dataCardTotalCount} match${dataCardTotalCount === 1 ? "" : "es"}`
                        : "Your existing cards"}
                  </span>
                </div>

                {normalizedQuery.length > 0 ? (
                  isTabLoading ? (
                    <div className="global-card-search__empty">Searching data cards...</div>
                  ) : cardResults.length > 0 ? (
                    <div className="global-card-search__list">
                      {cardResults.map((result) => (
                        <button
                          key={result.cardId}
                          type="button"
                          className="global-card-search__item"
                          onClick={() => handleExistingCardOpen(result)}
                        >
                          <span className="global-card-search__item-title">
                            {renderHighlightedText(result.cardName, deferredQuery)}
                          </span>
                          <span className="global-card-search__item-meta">
                            Template: {result.templateName}
                          </span>
                          <span className="global-card-search__item-meta global-card-search__item-meta--muted">
                            Category: {result.categoryName}
                          </span>
                        </button>
                      ))}
                      {hasMoreDataCards && (
                        <button
                          type="button"
                          className="global-card-search__load-more"
                          onClick={loadMoreDataCards}
                          disabled={isLoadingMoreDataCards}
                        >
                          {isLoadingMoreDataCards ? "Loading more..." : `Load more data cards (${cardResults.length} of ${dataCardTotalCount})`}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="global-card-search__empty">No matching data cards found.</div>
                  )
                ) : (
                  <div className="global-card-search__empty">Start typing to search your data cards by card name.</div>
                )}
              </section>
            ) : (
              <section className="global-card-search__section global-card-search__section--single" aria-labelledby="global-card-search-templates">
                <div className="global-card-search__section-header">
                  <h3 id="global-card-search-templates">Add New Cards</h3>
                  <span>
                    {isTabLoading
                      ? "Searching..."
                      : hasSearchTerm
                        ? `${templateTotalCount} match${templateTotalCount === 1 ? "" : "es"}`
                        : "Available templates"}
                  </span>
                </div>

                {normalizedQuery.length > 0 ? (
                  isTabLoading ? (
                    <div className="global-card-search__empty">Searching card templates...</div>
                  ) : templateResults.length > 0 ? (
                    <div className="global-card-search__list">
                      {templateResults.map((result) => (
                        <button
                          key={`${result.template.idCardTemplate}-${result.category?.id_Category ?? result.categoryName}`}
                          type="button"
                          className="global-card-search__item"
                          onClick={() => handleCreateCard(result)}
                        >
                          <span className="global-card-search__item-title">
                            {renderHighlightedText(result.template.templateName, deferredQuery)}
                          </span>
                          <span className="global-card-search__item-meta">Create new card</span>
                          <span className="global-card-search__item-meta global-card-search__item-meta--muted">
                            Category: {result.categoryName}
                          </span>
                        </button>
                      ))}
                      {hasMoreTemplates && (
                        <button
                          type="button"
                          className="global-card-search__load-more"
                          onClick={loadMoreTemplates}
                          disabled={isLoadingMoreTemplates}
                        >
                          {isLoadingMoreTemplates ? "Loading more..." : `Load more templates (${templateResults.length} of ${templateTotalCount})`}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="global-card-search__empty">No matching card templates found.</div>
                  )
                ) : (
                  <div className="global-card-search__empty">Start typing to find a template and create a new card.</div>
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { SearchIcon };
export default GlobalCardSearch;
